import React, { useEffect, useState } from 'react';
import { Newspaper, ExternalLink, ArrowRight, Loader2 } from 'lucide-react';

const newsItems = [
    {
        id: 1,
        title: "New Study Confirms Benefits of Intergenerational Connection",
        date: "December 12, 2025",
        category: "Research",
        summary: "Recent findings published in the Journal of Gerontology highlight significant mood improvements in seniors participating in regular video visits.",
        url: "#"
    },
    {
        id: 2,
        title: "Sage And Seed Expands to 50 New Schools",
        date: "November 28, 2025",
        category: "Community",
        summary: "High school students across the country are joining our mission to bridge the generational gap and find purpose in volunteering.",
        url: "#"
    },
    {
        id: 3,
        title: "Introducing Multilingual AI Support",
        date: "November 15, 2025",
        category: "Platform Update",
        summary: "Our new AI companion now supports real-time translation and cultural prompts for over 15 languages, making connection accessible to all.",
        url: "#"
    }
];

const NewsSection = () => {
    const [news, setNews] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchNews = async () => {
            try {
                const defaultUrl = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8000';
                const API_URL = process.env.REACT_APP_API_URL || defaultUrl;
                const response = await fetch(`${API_URL}/api/news`);
                if (response.ok) {
                    const data = await response.json();
                    if (data && data.length > 0) {
                        setNews(data);
                    } else {
                        setNews(newsItems); // Fallback to mocks
                    }
                } else {
                    setNews(newsItems);
                }
            } catch (err) {
                console.error("Fetch news failed", err);
                setNews(newsItems);
            } finally {
                setLoading(false);
            }
        };
        fetchNews();
    }, []);

    const formatDate = (dateStr) => {
        if (!dateStr) return '';
        if (isNaN(Date.parse(dateStr)) || dateStr.includes(' ')) return dateStr;
        const date = new Date(dateStr);
        return date.toLocaleDateString('en-US', {
            month: 'long',
            day: 'numeric',
            year: 'numeric'
        });
    };

    return (
        <section
            id="news"
            className="py-24 relative"
            style={{ background: '#F6F6F6' }}
        >
            <div className="max-w-7xl mx-auto px-6">
                <div className="text-center mb-16">
                    <h2
                        className="text-4xl md:text-5xl font-bold mb-6"
                        style={{ fontFamily: 'Merriweather, serif', color: '#163172' }}
                    >
                        Latest Updates
                    </h2>
                    <p
                        className="text-lg md:text-xl max-w-3xl mx-auto leading-relaxed"
                        style={{ color: '#000000' }}
                    >
                        Stories of connection, research breakthroughs, and community growth.
                        Stay informed about our journey to bridge the generational gap.
                    </p>
                </div>

                {/* News articles */}
                <div
                    className="flex space-x-6 overflow-x-auto pb-8 mb-4 px-4 -mx-4 custom-scrollbar"
                    style={{
                        scrollbarWidth: 'thin',
                        scrollbarColor: '#D6E4F0 transparent'
                    }}
                >
                    {news.map((item) => (
                        <div
                            key={item.id}
                            className="flex-shrink-0 w-[350px] md:w-[450px] p-8 rounded-2xl border border-[#D6E4F0] hover:bg-white transition-all duration-300 shadow-sm hover:shadow-md animate-in fade-in slide-in-from-right duration-500"
                            style={{ background: '#FFFFFF' }}
                        >
                            <div className="flex flex-col h-full">
                                <div className="flex items-center space-x-4 mb-6">
                                    <div
                                        className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                                        style={{ background: '#F6F6F6' }}
                                    >
                                        <Newspaper className="w-6 h-6" style={{ color: '#1E56A0' }} />
                                    </div>
                                    <div className="min-w-0">
                                        <h3 className="text-xl font-bold truncate" style={{ color: '#163172' }}>
                                            {item.title}
                                        </h3>
                                        <p className="text-sm font-medium" style={{ color: '#1E56A0' }}>
                                            {item.category} • {formatDate(item.date)}
                                        </p>
                                    </div>
                                </div>
                                <p className="leading-relaxed text-black mb-6 flex-1 line-clamp-4">
                                    {item.summary}
                                </p>

                                {item.url && (
                                    <div className="mt-auto pt-4 border-t border-[#F6F6F6]">
                                        <a
                                            href={item.url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="inline-flex items-center text-sm font-bold transition-all hover:underline"
                                            style={{ color: '#1E56A0' }}
                                        >
                                            Read Full Article
                                            <ExternalLink className="w-3.5 h-3.5 ml-1.5" />
                                        </a>
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}

                    {loading && (
                        <div className="flex items-center justify-center p-8 min-w-[200px]">
                            <Loader2 className="w-8 h-8 animate-spin text-[#1E56A0]" />
                        </div>
                    )}
                </div>
            </div>
        </section>
    );
};


export default NewsSection;
